import { useOutletContext, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { CheckCircle2, Clock, XCircle, AlertTriangle, ArrowRight } from "lucide-react";

const STATUS_CONFIG = {
  posted: {
    icon: CheckCircle2, iconColor: 'text-green-500',
    title: 'Charge Posted Successfully',
    message: "The charge has been posted to the guest's folio.",
    textColor: 'text-green-600', borderColor: 'border-green-200 bg-green-50',
    badgeColor: 'text-green-700', badgeText: 'Posted to PMS',
  },
  pending: {
    icon: Clock, iconColor: 'text-amber-500',
    title: 'Charge Saved, Posting Shortly',
    message: 'The charge will be posted to the hotel system shortly.',
    textColor: 'text-amber-600', borderColor: 'border-amber-200 bg-amber-50',
    badgeColor: 'text-amber-700', badgeText: 'Pending',
  },
  local_only: {
    icon: AlertTriangle, iconColor: 'text-amber-500',
    title: 'Charge Saved Locally',
    message: 'The charge could not be sent to the hotel system. It has been saved and the front desk will need to post it manually.',
    textColor: 'text-amber-600', borderColor: 'border-amber-200 bg-amber-50',
    badgeColor: 'text-amber-700', badgeText: 'Saved Locally',
  },
  failed: {
    icon: XCircle, iconColor: 'text-red-500',
    title: 'Could Not Post Charge',
    message: 'Please try again or report to supervisor.',
    textColor: 'text-red-600', borderColor: 'border-red-200 bg-red-50',
    badgeColor: 'text-red-700', badgeText: 'Failed',
  },
};

export default function Receipt() {
  const navigate = useNavigate();
  const { lastTransaction, setCurrentRoom, clearCart } = useOutletContext();

  const newRoom = () => {
    setCurrentRoom(null);
    clearCart();
    navigate("/pos/room");
  };

  if (!lastTransaction) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4" data-testid="no-receipt">
        <p className="text-gray-400 mb-4">No recent transaction</p>
        <Button onClick={newRoom} className="rounded-none bg-black text-white">New Room</Button>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[lastTransaction.status] || STATUS_CONFIG.failed;
  const Icon = cfg.icon;

  return (
    <div className="flex flex-col items-center justify-center h-full p-6" data-testid="receipt-page">
      <div className="w-full max-w-sm text-center animate-fade-in">
        <Icon className={`w-16 h-16 ${cfg.iconColor} mx-auto mb-4`} />

        <h2 className="font-heading text-xl font-black tracking-tight mb-2" data-testid="receipt-status">
          {cfg.title}
        </h2>

        <p className={`text-sm mb-6 ${cfg.textColor}`}>{cfg.message}</p>

        {lastTransaction.status === 'local_only' && lastTransaction.pms_error && (
          <div className="bg-amber-50 border border-amber-200 p-3 mb-4 text-left" data-testid="pms-error-detail">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-600 mb-1">PMS Error</p>
            <p className="text-xs text-amber-800">{lastTransaction.pms_error}</p>
          </div>
        )}

        <div className={`border-2 p-4 text-left mb-6 ${cfg.borderColor}`}>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <span className="text-gray-500">Room</span>
            <span className="font-bold text-right">{lastTransaction.room_number}</span>
            <span className="text-gray-500">Guest</span>
            <span className="font-medium text-right">{lastTransaction.guest_name}</span>
            <span className="text-gray-500">Reference</span>
            <span className="font-mono text-xs text-right" data-testid="receipt-ref">{lastTransaction.transaction_ref}</span>
            <span className="text-gray-500">Status</span>
            <span className={`text-right font-bold text-xs uppercase ${cfg.badgeColor}`} data-testid="receipt-status-badge">
              {cfg.badgeText}
            </span>
            <span className="text-gray-500">Total</span>
            <span className="font-heading text-xl font-black text-right" data-testid="receipt-total">
              ${lastTransaction.grand_total?.toFixed(2)}
            </span>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
            {lastTransaction.lines?.map((line, i) => (
              <div key={`${line.item_code}-${i}`} className="flex justify-between text-xs">
                <span>{line.quantity}x {line.item_name}</span>
                <span className="font-mono">${line.line_total?.toFixed(2)}</span>
              </div>
            ))}
            {lastTransaction.tax_total > 0 && (
              <div className="flex justify-between text-xs text-gray-500 pt-1">
                <span>Tax</span>
                <span className="font-mono">${lastTransaction.tax_total?.toFixed(2)}</span>
              </div>
            )}
            {lastTransaction.gratuity > 0 && (
              <div className="flex justify-between text-xs text-gray-500">
                <span>Service Charge</span>
                <span className="font-mono">${lastTransaction.gratuity?.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {lastTransaction.status === 'local_only' && (
          <div className="bg-gray-100 border border-gray-200 p-3 mb-4 text-left" data-testid="local-only-notice">
            <p className="text-xs text-gray-700 font-medium">
              This charge has been recorded for Room {lastTransaction.room_number}.
              Please notify the front desk to post this charge manually.
            </p>
            <p className="text-[10px] text-gray-500 mt-1">Ref: {lastTransaction.transaction_ref}</p>
          </div>
        )}

        <Button onClick={newRoom}
          className="w-full h-12 rounded-none bg-black text-white hover:bg-gray-800 font-bold text-sm"
          data-testid="new-room-button">
          New Room <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
